import { tick } from "@simplybusiness/icons";
import { Flex, Icon, Text } from "@simplybusiness/mobius";
import { PriceDetails } from "../../PriceDetails";
import { ExtraCoverage } from "../ExtraCoverage";
import {
  getCoverLimits,
  formatExcesses,
  extractCoverAmount,
  findHelpText,
} from "../transformCoverage";
import { Deductibles } from "../Deductibles";
import { CoverLimits } from "../../CoverLimits";
import { CoverLimit } from "../../CoverLimit";
import type { CoverHeaderProps, DisplayCoverProps } from "./types";
import type { DisplayVariant } from "..";

const CoverHeader = ({ coverage, display, helpText }: CoverHeaderProps) => (
  <Flex>
    <Icon icon={tick} className="mobius-quote-card__coverage-tick-icon" />
    <Text className="mobius-quote-card__coverage-header-title">
      {display.oneLineDisplay ? (
        <CoverLimit
          title={coverage.displayName}
          amount={extractCoverAmount(coverage.limits[0])}
          explainer={findHelpText(helpText, coverage.displayName)}
          withModal={true}
          isCoreCover={true}
        />
      ) : (
        coverage.displayName
      )}
    </Text>
  </Flex>
);

const showOnQcp = (
  variant: DisplayVariant,
  oneLine: boolean = false,
  qds: boolean = false,
) => !qds && (oneLine || variant === "qcp");

const showDeductibles = (name: string, variant: DisplayVariant) =>
  !(name === "Workers Compensation" && variant === "qcp");

export const DisplayCover = ({
  coverage,
  display,
  excess,
  displayVariant,
  helpText,
  priceDetails,
  extraCovers,
}: DisplayCoverProps) => {
  const shouldShowHeader =
    showOnQcp(displayVariant, display.oneLineDisplay, display.qdsDisplay) ||
    displayVariant === "qds";
  const shouldShowDeductibles =
    shouldShowHeader && showDeductibles(coverage.displayName, displayVariant);
  const shouldShowExtraCovers = extraCovers && extraCovers.length > 1;
  const shouldShowPriceDetails = displayVariant === "qcp" && priceDetails;

  return (
    <Flex
      key={coverage.coverName}
      flexGrow={1}
      alignItems="flex-start"
      className="mobius-quote-card__coverage-item"
    >
      <Flex flexDirection="column" flexGrow={1}>
        {shouldShowHeader && (
          <CoverHeader
            coverage={coverage}
            display={display}
            helpText={helpText}
          />
        )}

        {!display.oneLineDisplay && (
          <CoverLimits
            coverLimits={getCoverLimits(
              coverage.coverName,
              coverage.limits,
              display.filterQcpLimits || false,
              displayVariant,
              helpText,
              display.withModal,
            )}
          />
        )}

        {shouldShowPriceDetails && <PriceDetails priceDetails={priceDetails} />}

        {shouldShowExtraCovers && (
          <ExtraCoverage
            extraCovers={extraCovers}
            parentCover={coverage.coverName}
            variant={displayVariant}
            helpText={helpText}
          />
        )}

        {shouldShowDeductibles && (
          <Deductibles
            excesses={formatExcesses(
              coverage.displayExcess,
              displayVariant === "qcp"
                ? excess.quoteCardExcesses
                : excess.quoteSliderExcesses,
              helpText,
            )}
            excessTitle={coverage.excessTitle}
            coverName={coverage.coverName}
            variant={displayVariant}
          />
        )}
      </Flex>
    </Flex>
  );
};
